import { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Clock, TrendingDown,
  ArrowRight, CheckCircle2, Zap, IndianRupee,
  Quote,
} from "lucide-react";
import SectionLabel from "../ui/SectionLabel";
import BookAuditButton from "../ui/BookAuditButton";

/* ════════════════════════════════════════════════════════════
   CASE STUDY — Full transformation story
   ════════════════════════════════════════════════════════════ */

const companyProfile = {
  name: "A 300-Person Manufacturing Firm",
  industry: "Manufacturing & Distribution",
  employees: "300+",
  location: "Gujarat, India",
  challenge: "Senior operations team spending 60% of time on manual reporting, invoice tracking, and data entry instead of strategic work.",
};

const timeline = [
  {
    week: "Week 1-2",
    phase: "Discovery & Install",
    description: "Deployed monitoring agents across 6 departments. Zero disruption to existing workflows.",
    metric: "6 departments connected",
    icon: Building2,
    color: "var(--accent)",
  },
  {
    week: "Week 3-4",
    phase: "Data Collection",
    description: "Our software silently tracked 2,847 unique workflow patterns across the entire organisation.",
    metric: "2,847 workflows tracked",
    icon: Clock,
    color: "var(--accent)",
  },
  {
    week: "Week 5",
    phase: "Analysis Report",
    description: "Delivered a 23-page analysis. Identified 18 repetitive processes consuming 127 hours/week across teams.",
    metric: "18 automatable processes",
    icon: TrendingDown,
    color: "rgba(239,68,68,0.8)",
  },
  {
    week: "Week 6-10",
    phase: "Custom Build",
    description: "Built and deployed 7 custom AI agents: Invoice Tracker, Report Generator, CRM Sync, Email Follow-up, Meeting Summarizer, Inventory Alerts, Client Reporting.",
    metric: "7 AI agents deployed",
    icon: Zap,
    color: "var(--green)",
  },
  {
    week: "Week 12+",
    phase: "Results",
    description: "Operations team reclaimed 100+ hours/week. CFO reported \u20B910L/month in savings within first quarter.",
    metric: "\u20B910L/mo saved",
    icon: CheckCircle2,
    color: "var(--green)",
  },
];

const resultMetrics = [
  { label: "Hours Saved / Week", before: "127h manual", after: "11h manual", pct: "91%", icon: Clock },
  { label: "Monthly Cost Reduction", before: "\u20B922L/mo", after: "\u20B94L/mo", pct: "82%", icon: IndianRupee },
  { label: "Processes Automated", before: "0 of 18", after: "16 of 18", pct: "89%", icon: Zap },
  { label: "Team Redeployed to Strategy", before: "12 people on busywork", after: "12 people on growth", pct: "100%", icon: Users },
];

const agentsDeployed = [
  { name: "Invoice Tracker", result: "Reduced overdue invoices by 94%. Auto-sends reminders at 7, 14, 30 days." },
  { name: "Report Generator", result: "Weekly ops reports generated in 12 seconds instead of 6 hours." },
  { name: "CRM Auto-Sync", result: "Eliminated 15h/week of manual data entry between 3 systems." },
  { name: "Email Follow-up Bot", result: "Sends personalised follow-ups to 200+ clients. Response rate up 34%." },
  { name: "Meeting Summarizer", result: "Extracts action items from every meeting. Assigns tasks automatically." },
  { name: "Inventory Alert System", result: "Predicts stockouts 2 weeks in advance. Zero emergency orders since deploy." },
  { name: "Client Reporting Bot", result: "Sends branded reports to 47 clients monthly. Zero manual effort." },
];

/* ── Timeline Stage ── */
function TimelineItem({ item, index, isLast }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, amount: 0.4 });
  const Icon = item.icon;

  return (
    <div ref={ref} className="cs-tl-row">
      {/* Left — week label */}
      <motion.div
        className="cs-tl-left"
        initial={{ opacity: 0, x: -20 }}
        animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
        transition={{ duration: 0.4 }}
      >
        <span className="cs-tl-week" style={{ color: item.color }}>{item.week}</span>
        <span className="cs-tl-phase">{item.phase}</span>
      </motion.div>

      {/* Center — dot + line */}
      <div className="cs-tl-center">
        <motion.div
          className="cs-tl-dot"
          initial={{ scale: 0 }}
          animate={inView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.4, ease: "backOut" }}
          style={{
            background: inView ? item.color : "var(--bg-elevated)",
            boxShadow: inView
              ? `0 0 16px ${item.color === "var(--green)" ? "rgba(34,197,94,0.3)" : "var(--accent-glow)"}`
              : "none",
          }}
        >
          <Icon size={14} strokeWidth={2} style={{ color: "#fff" }} />
        </motion.div>
        {!isLast && (
          <motion.div
            className="cs-tl-line"
            initial={{ scaleY: 0 }}
            animate={inView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{
              background: `linear-gradient(180deg, ${item.color}, var(--border-default))`,
            }}
          />
        )}
      </div>

      {/* Right — content card */}
      <motion.div
        className={`cs-tl-card${isLast ? "" : " cs-tl-card-spaced"}`}
        initial={{ opacity: 0, x: 20 }}
        animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <p className="cs-tl-desc">{item.description}</p>
        <div
          className="cs-tl-metric-badge"
          style={{
            background: item.color === "var(--green)" ? "rgba(34,197,94,0.08)"
              : item.color === "rgba(239,68,68,0.8)" ? "rgba(239,68,68,0.06)" : "var(--accent-dim)",
            borderColor: item.color === "var(--green)" ? "rgba(34,197,94,0.15)"
              : item.color === "rgba(239,68,68,0.8)" ? "rgba(239,68,68,0.12)" : "rgba(40,180,174,0.15)",
          }}
        >
          <span style={{ color: item.color }}>{item.metric}</span>
        </div>
      </motion.div>
    </div>
  );
}

/* ── Agents Deployed Card ── */
function AgentCard({ agent, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, amount: 0.3 });

  return (
    <motion.div
      ref={ref}
      className="cs-agent-card"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
    >
      <div className="cs-agent-header">
        <span className="cs-agent-dot" />
        <span className="cs-agent-name">{agent.name}</span>
      </div>
      <p className="cs-agent-result">{agent.result}</p>
    </motion.div>
  );
}

/* ══════════════════════════════════════════
   Main Component
   ══════════════════════════════════════════ */
export default function TheProof() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [activeTab, setActiveTab] = useState("timeline");

  return (
    <section
      id="proof"
      ref={sectionRef}
      style={{
        background: "var(--bg-base)",
        padding: "var(--section-padding-y) var(--container-padding)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient glow */}
      <div className="cs-ambient" />

      <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", position: "relative" }}>
        <SectionLabel text="REAL RESULTS" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: "40px" }}
        >
          <h2 className="cs-heading">
            How we transformed{" "}
            <span className="cs-heading-accent">a 300-person manufacturing firm.</span>
          </h2>
          <p className="cs-sub">
            Not a hypothetical. Not a pitch. Here's exactly what happened when we
            deployed across a 300-person manufacturing firm.
          </p>
        </motion.div>

        {/* Company profile strip */}
        <motion.div
          className="cs-profile"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {[
            { icon: Building2, label: "Industry", value: companyProfile.industry },
            { icon: Users, label: "Team Size", value: companyProfile.employees },
            { icon: Clock, label: "Challenge", value: "60% time on manual work" },
            { icon: TrendingDown, label: "Goal", value: "Automate repetitive ops" },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="cs-profile-card">
                <div className="cs-profile-icon">
                  <Icon size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="cs-profile-label">{item.label}</div>
                  <div className="cs-profile-value">{item.value}</div>
                </div>
              </div>
            );
          })}
        </motion.div>

        {/* Tab navigation */}
        <div className="cs-tabs">
          {[
            { key: "timeline", label: "The Journey" },
            { key: "agents", label: "Agents Deployed" },
            { key: "results", label: "Final Results" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`cs-tab${activeTab === tab.key ? " cs-tab-active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ Tab: Timeline ═══ */}
        {activeTab === "timeline" && (
          <div className="cs-timeline">
            {timeline.map((item, i) => (
              <TimelineItem
                key={i}
                item={item}
                index={i}
                isLast={i === timeline.length - 1}
              />
            ))}
          </div>
        )}

        {/* ═══ Tab: Agents Deployed ═══ */}
        {activeTab === "agents" && (
          <div>
            <p className="cs-agents-intro">
              7 custom AI agents were built specifically for this organisation.
              Each one replaced hours of manual work every single week.
            </p>

            {/* Dashboard screenshot */}
            <motion.div
              className="cs-dashboard-wrap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img
                src="/resource-optimizer-hold/Real_screen_recording.png"
                alt="Agent Activity Logs dashboard — live"
                className="cs-dashboard-img"
              />
              <div className="cs-dashboard-overlay" />
              <div className="cs-dashboard-badge">
                <span className="cs-dashboard-dot" />
                Agent Activity Logs — Live
              </div>
              <div className="cs-dashboard-caption">
                This is what the system looks like running inside the client's environment
              </div>
            </motion.div>

            <div className="cs-agents-grid">
              {agentsDeployed.map((agent, i) => (
                <AgentCard key={agent.name} agent={agent} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ═══ Tab: Final Results ═══ */}
        {activeTab === "results" && (
          <div>
            {/* Result metrics */}
            <div className="cs-results-grid">
              {resultMetrics.map((m, i) => {
                const Icon = m.icon;
                return (
                  <motion.div
                    key={m.label}
                    className="cs-result-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                  >
                    <div className="cs-result-top">
                      <div className="cs-result-top-left">
                        <div className="cs-result-icon">
                          <Icon size={14} strokeWidth={1.5} />
                        </div>
                        <span className="cs-result-label">{m.label}</span>
                      </div>
                      <span className="cs-result-pct">-{m.pct}</span>
                    </div>

                    <div className="cs-result-compare">
                      <div className="cs-result-col">
                        <span className="cs-result-compare-label">Before</span>
                        <span className="cs-result-before">{m.before}</span>
                      </div>
                      <ArrowRight size={16} className="cs-result-arrow" />
                      <div className="cs-result-col">
                        <span className="cs-result-compare-label">After</span>
                        <span className="cs-result-after">{m.after}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Client quote */}
            <motion.div
              className="cs-quote"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="cs-quote-glow" />
              <p className="cs-quote-text">
                "We were skeptical. We'd tried Zapier, tried hiring more people, tried
                everything. In 10 weeks, ResourceOptimizer found waste we didn't even know existed
                and eliminated it. Our ops team now focuses on growth instead of data entry."
              </p>
              <div className="cs-quote-author">
                <div className="cs-quote-avatar">VP</div>
                <div>
                  <div className="cs-quote-name">VP of Operations</div>
                  <div className="cs-quote-role">300-person Manufacturing Firm, Gujarat</div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Bottom CTA strip */}
        <div className="cs-bottom-cta">
          <div>
            <p className="cs-bottom-title">
              Want results like this for your organisation?
            </p>
            <p className="cs-bottom-sub">
              We'll show you what's possible in a 30-minute audit. No cost. No commitment.
            </p>
          </div>
          <BookAuditButton variant="compact" className="cs-bottom-btn" />
        </div>
      </div>

      <style>{`
        /* ── Ambient ── */
        .cs-ambient {
          position: absolute;
          width: 700px;
          height: 700px;
          background: radial-gradient(circle, rgba(34,197,94,0.04) 0%, transparent 70%);
          top: 20%;
          right: -200px;
          filter: blur(100px);
          pointer-events: none;
        }

        /* ── Heading ── */
        .cs-heading {
          font-weight: 800;
          font-size: clamp(32px, 4.5vw, 52px);
          line-height: 1.1;
          color: var(--text-primary);
          margin: 8px 0 14px;
          letter-spacing: -0.03em;
        }

        .cs-heading-accent {
          color: var(--accent);
        }

        .cs-sub {
          font-size: 16px;
          color: var(--text-secondary);
          max-width: 560px;
          line-height: 1.6;
          margin: 0;
        }

        /* ── Profile strip ── */
        .cs-profile {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 36px;
        }

        .cs-profile-card {
          padding: 16px;
          border-radius: 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          display: flex;
          align-items: flex-start;
          gap: 10px;
          transition: all 0.3s ease;
        }

        .cs-profile-card:hover {
          border-color: rgba(40,180,174,0.2);
          transform: translateY(-2px);
        }

        .light .cs-profile-card {
          background: #FFFFFF;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }

        .cs-profile-icon {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: var(--accent-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent);
          flex-shrink: 0;
        }

        .cs-profile-label {
          font-size: 9px;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 2px;
        }

        .cs-profile-value {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          line-height: 1.3;
        }

        /* ── Tabs ── */
        .cs-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 32px;
          padding: 4px;
          border-radius: 12px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          width: fit-content;
        }

        .light .cs-tabs {
          background: #FFFFFF;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }

        .cs-tab {
          padding: 8px 20px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-muted);
          background: transparent;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cs-tab:hover {
          color: var(--text-primary);
        }

        .cs-tab-active {
          font-weight: 600;
          color: #fff !important;
          background: var(--accent) !important;
        }

        /* ══════════════════════════════
           TIMELINE
           ══════════════════════════════ */
        .cs-timeline {
          max-width: 700px;
        }

        .cs-tl-row {
          display: grid;
          grid-template-columns: 120px 48px 1fr;
          gap: 0;
          min-height: 120px;
        }

        .cs-tl-left {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          padding-right: 16px;
          padding-top: 8px;
        }

        .cs-tl-week {
          font-weight: 800;
          font-size: 14px;
          letter-spacing: -0.02em;
        }

        .cs-tl-phase {
          font-size: 10px;
          font-weight: 500;
          color: var(--text-muted);
          margin-top: 2px;
        }

        .cs-tl-center {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .cs-tl-dot {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          border: 3px solid var(--bg-base);
          transition: all 0.4s ease;
          flex-shrink: 0;
        }

        .cs-tl-line {
          width: 2px;
          flex: 1;
          min-height: 60px;
          transform-origin: top;
          opacity: 0.4;
        }

        .cs-tl-card {
          padding: 14px 18px;
          border-radius: 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          margin-left: 12px;
        }

        .cs-tl-card-spaced {
          margin-bottom: 12px;
        }

        .light .cs-tl-card {
          background: #FFFFFF;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04);
        }

        .cs-tl-desc {
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1.6;
          margin: 0 0 8px;
        }

        .cs-tl-metric-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 6px;
          border: 1px solid;
        }

        .cs-tl-metric-badge span {
          font-weight: 700;
          font-size: 12px;
        }

        /* ══════════════════════════════
           AGENTS TAB
           ══════════════════════════════ */
        .cs-agents-intro {
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 20px;
          line-height: 1.6;
        }

        /* Dashboard screenshot */
        .cs-dashboard-wrap {
          position: relative;
          width: 100%;
          border-radius: 18px;
          overflow: hidden;
          margin-bottom: 20px;
          border: 1px solid var(--border-default);
          box-shadow: 0 4px 24px rgba(0,0,0,0.20), 0 0 0 1px rgba(40,180,174,0.07);
        }

        .light .cs-dashboard-wrap {
          border-color: rgba(0,0,0,0.08);
          box-shadow: 0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(40,180,174,0.06);
        }

        .cs-dashboard-img {
          width: 100%;
          display: block;
          object-fit: cover;
          max-height: 320px;
          filter: saturate(0.9) brightness(0.95);
          transition: filter 0.3s ease;
        }

        .cs-dashboard-wrap:hover .cs-dashboard-img {
          filter: saturate(1) brightness(1);
        }

        .light .cs-dashboard-img {
          filter: saturate(1) brightness(1);
        }

        .cs-dashboard-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(13,15,26,0.08) 0%, rgba(13,15,26,0.50) 100%);
          pointer-events: none;
        }

        .light .cs-dashboard-overlay {
          background: linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.30) 100%);
        }

        .cs-dashboard-badge {
          position: absolute;
          top: 14px;
          left: 14px;
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 11px;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          letter-spacing: 0.03em;
          background: rgba(13,15,26,0.65);
          border: 1px solid rgba(255,255,255,0.10);
          backdrop-filter: blur(10px);
          padding: 6px 13px;
          border-radius: 100px;
        }

        .light .cs-dashboard-badge {
          background: rgba(255,255,255,0.82);
          border-color: rgba(0,0,0,0.10);
          color: rgba(0,0,0,0.75);
        }

        .cs-dashboard-dot {
          display: block;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #22C55E;
          box-shadow: 0 0 8px rgba(34,197,94,0.9);
          animation: cs-pulse-glow 1.6s ease infinite;
          flex-shrink: 0;
        }

        @keyframes cs-pulse-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        .cs-dashboard-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          font-size: 11px;
          color: rgba(255,255,255,0.55);
          padding: 10px 16px 12px;
          background: linear-gradient(0deg, rgba(13,15,26,0.70) 0%, transparent 100%);
          text-align: center;
        }

        /* Agents grid */
        .cs-agents-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .cs-agent-card {
          padding: 16px 18px;
          border-radius: 14px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          transition: border-color 0.3s ease, transform 0.3s ease;
          cursor: default;
        }

        .cs-agent-card:hover {
          border-color: rgba(40,180,174,0.25);
          transform: translateY(-4px);
        }

        .light .cs-agent-card {
          background: #FFFFFF;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(40,180,174,0.05);
        }

        .cs-agent-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .cs-agent-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--green);
          box-shadow: 0 0 6px rgba(34,197,94,0.4);
          flex-shrink: 0;
        }

        .cs-agent-name {
          font-weight: 700;
          font-size: 14px;
          color: var(--text-primary);
        }

        .cs-agent-result {
          font-size: 12px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0;
        }

        /* ══════════════════════════════
           RESULTS TAB
           ══════════════════════════════ */
        .cs-results-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px;
          margin-bottom: 28px;
        }

        .cs-result-card {
          padding: 20px;
          border-radius: 16px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          transition: all 0.3s ease;
        }

        .cs-result-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 32px rgba(40,180,174,0.08);
        }

        .light .cs-result-card {
          background: #FFFFFF;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(40,180,174,0.05);
        }

        .cs-result-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .cs-result-top-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .cs-result-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          background: var(--accent-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--accent);
        }

        .cs-result-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .cs-result-pct {
          font-weight: 800;
          font-size: 18px;
          color: var(--green);
          padding: 2px 8px;
          border-radius: 6px;
          background: rgba(34,197,94,0.08);
        }

        .cs-result-compare {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .cs-result-col {
          flex: 1;
        }

        .cs-result-compare-label {
          display: block;
          font-size: 10px;
          color: var(--text-muted);
          margin-bottom: 2px;
        }

        .cs-result-before {
          font-weight: 700;
          font-size: 15px;
          color: var(--text-muted);
          text-decoration: line-through;
          opacity: 0.6;
        }

        .cs-result-after {
          font-weight: 800;
          font-size: 16px;
          color: var(--green);
        }

        .cs-result-arrow {
          color: var(--accent);
          flex-shrink: 0;
        }

        /* ── Quote ── */
        .cs-quote {
          padding: 24px 28px;
          border-radius: 16px;
          background: var(--bg-elevated);
          border: 1px solid var(--border-default);
          border-left: 3px solid var(--accent);
          position: relative;
          overflow: hidden;
        }

        .light .cs-quote {
          background: #FFFFFF;
          box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 6px 24px rgba(40,180,174,0.06);
        }

        .cs-quote-glow {
          position: absolute;
          top: -1px;
          left: -1px;
          width: 100px;
          height: 100px;
          background: radial-gradient(circle, var(--accent-dim) 0%, transparent 70%);
          pointer-events: none;
          border-radius: 16px;
        }

        .cs-quote-text {
          font-weight: 600;
          font-size: clamp(16px, 2vw, 20px);
          line-height: 1.5;
          color: var(--text-primary);
          position: relative;
          margin: 0 0 12px;
        }

        .cs-quote-author {
          display: flex;
          align-items: center;
          gap: 10px;
          position: relative;
        }

        .cs-quote-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--accent-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: var(--accent);
          flex-shrink: 0;
        }

        .cs-quote-name {
          font-weight: 600;
          font-size: 13px;
          color: var(--text-primary);
        }

        .cs-quote-role {
          font-size: 12px;
          color: var(--text-muted);
        }

        /* ── Bottom CTA ── */
        .cs-bottom-cta {
          margin-top: 40px;
          padding: 20px 28px;
          border-radius: 16px;
          background: var(--accent-dim);
          border: 1px solid rgba(40,180,174,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .cs-bottom-title {
          font-weight: 700;
          font-size: 17px;
          color: var(--text-primary);
          margin: 0 0 2px;
        }

        .cs-bottom-sub {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0;
        }

        .cs-bottom-btn {
          padding: 12px 28px !important;
          font-size: 14px !important;
          border-radius: 10px !important;
        }

        /* ══════════════════════════════
           Responsive
           ══════════════════════════════ */
        @media (max-width: 768px) {
          .cs-profile {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .cs-agents-grid {
            grid-template-columns: 1fr !important;
          }
          .cs-results-grid {
            grid-template-columns: 1fr !important;
          }
          .cs-dashboard-img {
            max-height: 200px;
          }
          .cs-tl-row {
            grid-template-columns: 80px 40px 1fr;
          }
          .cs-tl-week {
            font-size: 12px;
          }
          .cs-tl-phase {
            font-size: 9px;
          }
        }

        @media (max-width: 480px) {
          .cs-profile {
            grid-template-columns: 1fr !important;
          }
          .cs-tl-row {
            grid-template-columns: 1fr;
            gap: 8px;
          }
          .cs-tl-left {
            align-items: flex-start;
            padding-right: 0;
            flex-direction: row;
            gap: 8px;
          }
          .cs-tl-center {
            display: none;
          }
          .cs-tl-card {
            margin-left: 0;
          }
          .cs-tabs {
            width: 100%;
          }
          .cs-tab {
            flex: 1;
            text-align: center;
            padding: 8px 12px;
            font-size: 12px;
          }
        }
      `}</style>
    </section>
  );
}
